import React, { useEffect, useRef } from 'react';

/* One timeline at a time, both in the same pinned frame.

   Each act has four phases, and they are deliberately separate so the
   payoff never lands on top of something still being read:

     fill    the stills light up and the wires draw   (--f)
     read    everything sits lit, sharp and still
     reveal  the track blurs back and the figure lands (--v)
     fade    the act hands over to the next one        (--o)

   Both acts fill over the same 0.28 of the scroll — the comparison is
   in the figures, not in how fast the animation runs. */

const TRACKS = [
  {
    key: 'without',
    tone: 'tl-row-slow',
    name: 'Still doing EB work by hand?',
    lead: <>Still doing EB work <span className="gradient-text">by hand?</span></>,
    total: '40+',
    unit: 'hours per client',
    fill: (p) => clamp01(p / 0.28),
    reveal: (p) => clamp01((p - 0.34) / 0.06),
    fade: (p) => 1 - clamp01((p - 0.46) / 0.06),
    steps: [
      ['/step-messyfiles.webp', 'Chase files across portals'],
      ['/step-manualbenchmarks.webp', 'Look up benchmarks manually'],
      ['/step-revisedeck.webp', 'Rebuild the deck each revision'],
    ],
  },
  {
    key: 'with',
    tone: 'tl-row-fast',
    name: 'With Avenir',
    lead: <>With Avenir, renewals are done <span className="gradient-text">in a fraction of the time</span></>,
    total: '<1',
    unit: 'hour per client',
    fill: (p) => clamp01((p - 0.54) / 0.28),
    reveal: (p) => clamp01((p - 0.88) / 0.06),
    fade: (p) => clamp01((p - 0.48) / 0.06),
    steps: [
      ['/step-connectsources.webp', 'Connect your sources once'],
      ['/step-workflows.webp', 'Choose a workflow'],
      ['/step-brandedreport.webp', 'Branded report, exported'],
    ],
  },
];

function clamp01(n) { return n < 0 ? 0 : n > 1 ? 1 : n; }

const SplitTimeline = () => {
  const wrapRef = useRef(null);
  const rowRefs = useRef([]);
  const headRefs = useRef([]);

  const setRow = (i) => (el) => { rowRefs.current[i] = el; };
  const setHead = (i) => (el) => { headRefs.current[i] = el; };

  useEffect(() => {
    const wrap = wrapRef.current;
    if (!wrap) return undefined;

    const reduced = window.matchMedia('(prefers-reduced-motion: reduce)');
    const pinned = window.matchMedia('(min-width: 901px)');

    const apply = () => {
      // Stacked (phone / reduced motion): nothing is pinned, so each track
      // runs off its own position in the viewport. The steps still light up
      // and the wires still draw — the comparison is the whole point, and a
      // frozen end state does not read as before and after.
      if (!pinned.matches || reduced.matches) {
        const still = reduced.matches;
        const vh = window.innerHeight;

        TRACKS.forEach((t, i) => {
          const el = rowRefs.current[i];
          if (!el) return;
          el.style.setProperty('--o', '1');
          if (still) {
            el.style.setProperty('--f', '1');
            el.style.setProperty('--v', '1');
            return;
          }
          const r = el.getBoundingClientRect();
          const q = clamp01((vh * 0.82 - r.top) / (r.height + vh * 0.35));
          // fill over the first 70%, then the figure resolves
          el.style.setProperty('--f', clamp01(q / 0.7).toFixed(4));
          el.style.setProperty('--v', clamp01((q - 0.78) / 0.14).toFixed(4));
        });

        // the stacked layout carries its own per-track headings
        headRefs.current.forEach((h, i) => h && h.style.setProperty('--o', i === 0 ? '1' : '0'));
        return;
      }

      const rect = wrap.getBoundingClientRect();
      const travel = rect.height - window.innerHeight;
      const p = travel > 0 ? clamp01(-rect.top / travel) : 0;

      TRACKS.forEach((t, i) => {
        const o = t.fade(p).toFixed(4);

        const el = rowRefs.current[i];
        if (el) {
          el.style.setProperty('--f', t.fill(p).toFixed(4));
          el.style.setProperty('--v', t.reveal(p).toFixed(4));
          el.style.setProperty('--o', o);
        }

        // the question rides the manual track's fade, the answer the Avenir one
        const head = headRefs.current[i];
        if (head) head.style.setProperty('--o', o);
      });
    };

    // Synchronous: animation frames do not run while the tab is unpainted.
    apply();
    window.addEventListener('scroll', apply, { passive: true });
    window.addEventListener('resize', apply);
    document.addEventListener('visibilitychange', apply);
    reduced.addEventListener?.('change', apply);
    pinned.addEventListener?.('change', apply);

    return () => {
      window.removeEventListener('scroll', apply);
      window.removeEventListener('resize', apply);
      document.removeEventListener('visibilitychange', apply);
      reduced.removeEventListener?.('change', apply);
      pinned.removeEventListener?.('change', apply);
    };
  }, []);

  return (
    <div className="tl-scroll" ref={wrapRef}>
      <div className="tl-sticky">
        <div className="tl-head-block section-center">
          <h2 className="section-heading tl-heading">
            <span className="tl-phrase" ref={setHead(0)} style={{ '--o': 1 }}>
              Still doing EB work <span className="gradient-text">by hand?</span>
            </span>
            <span className="tl-phrase" ref={setHead(1)} style={{ '--o': 0 }}>
              With Avenir, renewals are done <span className="gradient-text">in a fraction of the time</span>
            </span>
          </h2>
        </div>

        <div className="tl">
          {TRACKS.map((t, i) => (
            <div
              key={t.key}
              ref={setRow(i)}
              className={`tl-row ${t.tone}`}
              style={{ '--f': 0, '--v': 0, '--o': i === 0 ? 1 : 0 }}
            >
              <p className="tl-row-head">{t.lead}</p>

              <div className="tl-track">
                <div className="tl-shots">
                  {t.steps.map(([img, label], n) => (
                    <figure
                      key={label}
                      className="tl-shot"
                      style={{ '--t': (n + 0.5) / t.steps.length, '--ct': (n + 0.5) / t.steps.length }}
                    >
                      <span className="tl-shot-frame">
                        <img src={img} alt="" />
                      </span>
                    </figure>
                  ))}
                </div>

                <div className="tl-captions">
                  {t.steps.map(([, label], n) => (
                    <span
                      key={label}
                      className="tl-caption"
                      style={{ '--t': (n + 0.5) / t.steps.length }}
                    >
                      {label}
                    </span>
                  ))}
                </div>
              </div>

              {/* the verdict lands over the finished track, which blurs behind it */}
              <div className="tl-verdict">
                <strong>{t.total}</strong>
                <span className="tl-verdict-unit">{t.unit}</span>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

export default SplitTimeline;
